/**
 * AttachmentUploader Component
 * Handles PDF file uploads for email drafts
 */

import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { Paperclip, X, FileText, Loader2, Save } from 'lucide-react';

/**
 * Format file size to human-readable format
 */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function AttachmentUploader({
  draftUuid,
  attachments = [],
  onUploadSuccess,
  onDeleteSuccess,
  onAutoSaveDraft // Function to auto-save draft if not saved yet
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null); // UUID of attachment being deleted
  const [savingDraft] = useState(false);

  // Constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB

  /**
   * Calculate total size of current attachments
   */
  const getTotalSize = () => {
    return attachments.reduce((total, att) => total + att.file_size, 0);
  };

  /**
   * Handle file selection and upload
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input
    event.target.value = '';

    // Validate file type (client-side check)
    if (file.type !== 'application/pdf') {
      toast({
        title: t('email.invalidFileType'),
        description: t('email.onlyPdfAllowed'),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }

    // Validate file extension
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: t('email.invalidFileType'),
        description: t('email.onlyPdfAllowed'),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: t('email.fileTooLarge'),
        description: t('email.maxFileSize', { size: '10MB' }),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }

    // Validate total size
    const currentTotal = getTotalSize();
    if (currentTotal + file.size > MAX_TOTAL_SIZE) {
      toast({
        title: t('email.totalSizeTooLarge'),
        description: t('email.maxTotalSize', { size: '25MB' }),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }

    // Upload file
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.emails.uploadAttachment(draftUuid, formData);

      toast({
        title: t('email.fileUploaded'),
        description: file.name,
        variant: 'success',
        duration: 3000,
      });

      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

    } catch (error) {
      console.error('Upload error:', error);

      let errorMessage = t('email.uploadFailed');
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.file) {
        errorMessage = error.response.data.file[0];
      }

      toast({
        title: t('email.uploadFailed'),
        description: errorMessage,
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle attachment deletion
   */
  const handleDelete = async (attachmentUuid, filename) => {
    setDeleting(attachmentUuid);

    try {
      await api.emails.deleteAttachment(draftUuid, attachmentUuid);

      toast({
        title: t('email.attachmentDeleted'),
        description: filename,
        variant: 'success',
        duration: 3000,
      });

      // Notify parent component
      if (onDeleteSuccess) {
        onDeleteSuccess(attachmentUuid);
      }

    } catch (error) {
      console.error('Delete error:', error);

      toast({
        title: t('email.deleteFailed'),
        description: error.response?.data?.error || t('email.deleteFailed'),
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setDeleting(null);
    }
  };

  /**
   * Handle download attachment
   */
  const handleDownload = async (attachmentUuid, filename) => {
    try {
      const response = await api.emails.downloadAttachment(draftUuid, attachmentUuid);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);

      toast({
        title: t('email.downloadFailed'),
        description: filename,
        variant: 'destructive',
        duration: 5000,
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Save Draft button - only show if draft not saved yet */}
          {!draftUuid && onAutoSaveDraft && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onAutoSaveDraft}
              disabled={savingDraft}
            >
              {savingDraft ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('email.savingDraft')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('email.saveDraftToAddAttachments')}
                </>
              )}
            </Button>
          )}

          {/* Add Attachment button - disabled until draft is saved */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={!draftUuid || uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Paperclip className="w-4 h-4 mr-2" />
            )}
            {uploading ? t('email.uploadingFile') : t('email.addAttachment')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
          />
        </div>

        {/* Total size indicator */}
        {attachments.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            {formatFileSize(getTotalSize())} / 25MB
          </Badge>
        )}
      </div>

      {/* File type info */}
      <p className="text-xs text-gray-500">
        {t('email.pdfOnlyInfo')} {t('email.maxFileSizeInfo', { size: '10MB' })}
      </p>

      {/* Attachments List */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.uuid}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600"
                    onClick={() => handleDownload(attachment.uuid, attachment.filename)}
                    title={attachment.filename}
                  >
                    {attachment.filename}
                  </p>
                  <p className="text-xs text-gray-500">
                    {attachment.file_size_display || formatFileSize(attachment.file_size)}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(attachment.uuid, attachment.filename)}
                  disabled={deleting === attachment.uuid}
                  className="flex-shrink-0"
                >
                  {deleting === attachment.uuid ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  ) : (
                    <X className="w-4 h-4 text-gray-500 hover:text-red-600" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No attachments message */}
      {attachments.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          {t('email.noAttachments')}
        </p>
      )}
    </div>
  );
}
